import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import styles from './LimitedCodes.scss'
import { QRCodeContainer } from '../QRCodeContainer'
import { shortcutMessages } from '../intlMessages'
import { CTA } from 'common/styleguide/CTA'
import { Icon } from 'common/Icon'
import { GENERAL_MODAL } from 'common/modals/GeneralModal'
import { messages as dateTimeMessages } from 'common/time/DateTime'
import { useFormatMessage, Msg, commonVerbs, commonActions } from 'lib/localization'
import { getBaseCurrentUser } from 'lib/utils'
import { showModal } from 'lib/modalUtils'
import { AppCode } from 'data/records'
import { FormattedMessage as T, defineMessages } from 'react-intl'
import moment from 'moment'
import _ from 'lodash'

export const messages = defineMessages({
  generateCodes: {
    id: 'LimitedCodes.generateCodes',
    defaultMessage: 'Generate limited use codes'
  },
  qrCode: {
    id: 'LimitedCodes.qrCode',
    defaultMessage: 'QR Code'
  },
  disable: {
    id: 'LimitedCodes.disable',
    defaultMessage: 'Disable'
  },
  disabled: {
    id: 'LimitedCodes.disabled',
    defaultMessage: 'Disabled'
  },
  remove: {
    id: 'LimitedCodes.remove',
    defaultMessage: 'Remove'
  },
  unlimitedMismatch: {
    id: 'LimitedCodes.unlimitedMismatch',
    defaultMessage: "To generate limited use codes please click 'Save'"
  }
})

export const LimitedCodes = props => {
  return (
    <div className={styles.limitedCodes}>
      <GenerateCodes {...props} />

      <hr />

      <CodesTable {...props} />
    </div>
  )
}

const readDateFormatFromPreferences = () => date => {
  const m = moment(date)
  let dateFmt
  switch (getBaseCurrentUser().preferences.date_format) {
    case 'dmy':
      dateFmt = 'DD MMM, YYYY'
      break
    case 'ymd':
      dateFmt = 'YYYY MMM DD'
      break
    default: // mdy
      dateFmt = 'MMM DD YYYY'
      break
  }

  return <Msg m={dateTimeMessages.dateTime} values={{ date: m.format(dateFmt), time: m.format('hh:mm a') }} />
}

export const CodesTable = ({ experience, appCodes, updateAppCode, deleteAppCode }) => {
  const dateFn = readDateFormatFromPreferences()
  return (
    <div className={styles.codesTable}>
      <div className={styles.header}>
        <h3>
          <T
            id='CodesTable.adjustDetails'
            defaultMessage='Adjust limited use code details' />
        </h3>
        <CTA href={`/app_codes.csv?parent_type=Experience&parent_id=${experience.id}`} secondary margins={false} style={{ maxWidth: '200px' }}>
          <Icon size={20} i='md-download' /><Msg m={commonActions.exportCSV} />
        </CTA>
      </div>

      <table className={styles.codesTable}>
        <thead>
          <tr>
            <th><Msg m={shortcutMessages.code} /></th>
            <th><T id='LimitedCodes.dateCreated' defaultMessage='Date Created' /></th>
            <th><T id='LimitedCodes.accessLimit' defaultMessage='Access Limit' /></th>
            <th><T id='LimitedCodes.timesUsed' defaultMessage='Times Used' /></th>
            <th><T id='LimitedCodes.actions' defaultMessage='Actions' /></th>
          </tr>
        </thead>
        <tbody>
          {appCodes.valueSeq().map((code, idx) => (
            <CodeRow
              key={idx}
              experience={experience}
              code={code}
              updateAppCode={updateAppCode}
              deleteAppCode={deleteAppCode}
              dateFn={dateFn} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const CodeRow = ({ experience, code, updateAppCode, deleteAppCode, dateFn }) => {
  const [accessLimit, setAccessLimit] = useState(code.accessLimit)

  useEffect(() => {
    setAccessLimit(code.accessLimit)
  }, [code])

  const debouncedAccessLimitUpdate = _.debounce(() => {
    if (experience.unlimited) { return }
    const params = AppCode.serialize(AppCode({ ...code, accessLimit }), ['accessLimit'])
    updateAppCode(code.id, params)
  }, 500)

  const disable = () => {
    if (experience.unlimited) { return }
    const params = AppCode.serialize(AppCode({ ...code, accessLimit: code.timesUsed }), ['accessLimit'])
    updateAppCode(code.id, params)
      .then(() => { setAccessLimit(code.timesUsed) })
      .catch(e => {
        window.alert(e.details[0].error_message)
      })
  }

  const onAccessLimitSubmit = e => {
    e.preventDefault()
    debouncedAccessLimitUpdate()
  }

  const remove = () => {
    if (experience.unlimited) { return }
    deleteAppCode(code.id)
      .catch(e => {
        window.alert(e.details[0].error_message)
      })
  }

  const qrComponent = <div className={styles.modalContainer}>
    <QRCodeContainer
      centered
      experienceId={experience.id}
      appCode={code}
      disabled={experience.unlimited} />
  </div>

  return (
    <tr>
      <td><b>{code.code}</b></td>
      <td>{dateFn(code.createdAt)}</td>
      <td style={{ width: '110px' }}>
        <form onSubmit={onAccessLimitSubmit}>
          <input
            type='number'
            min={code.timesUsed}
            max='200'
            style={{ width: '100%', margin: '0px', padding: '0px 5px' }}
            value={accessLimit}
            onBlur={onAccessLimitSubmit} // also fire PATCH when focus leaves
            onChange={e => setAccessLimit(e.target.value)} />
        </form>
      </td>
      <td style={Object.assign({ width: '110px' }, code.accessLimit === code.timesUsed && { color: '#ED4C49', fontWeight: 'bold' })}>{code.timesUsed}</td>
      <td>
        <div className={styles.actionsTd}>
          <Link to={`/experiences/${experience.id}?app_code=${code.code}`}><Icon i='md-share-alt' size={20} /> <Msg m={commonVerbs.share} /></Link>
          <span onClick={() => showModal(GENERAL_MODAL, { component: qrComponent, title: code.code })}><Icon i='qrcode-scan' size={20} /> <Msg m={messages.qrCode} /></span>
          {code.timesUsed > 0
            ? <>
              {code.timesUsed === code.accessLimit
                ? <span style={{ color: '#ED4C49', cursor: 'initial' }}><Icon i='md-remove-circle' size={20} /> <Msg m={messages.disabled} /></span>
                : <span onClick={disable}><Icon i='md-trash' size={20} /> <Msg m={messages.disable} /></span>}
            </>
            : <span onClick={remove}><Icon i='md-trash' size={20} /> <Msg m={messages.remove} /></span>}
        </div>
      </td>
    </tr>
  )
}

const GenerateCodes = ({ experience, createAppCodes }) => {
  const [num, setNum] = useState(1)
  const [requestPending, setRequestPending] = useState(false)
  const disabled = requestPending || experience.unlimited
  const fm = useFormatMessage()

  const onSubmit = async () => {
    if (disabled) return
    await setRequestPending(true)
    await createAppCodes(num)
      .finally(() => setRequestPending(false))
    setNum(1)
  }

  return (
    <div className={styles.generateCodes}>
      <h3><Msg m={messages.generateCodes} /></h3>
      <T
        id='LimitedCodes.generateCodesText'
        defaultMessage='Set number of limited use shortcut codes you want to generate.' />
      <form onSubmit={onSubmit} className={styles.generateForm}>
        <input type='number'
          style={{ margin: '0px 10px 0px 0px', flex: '1 0 80px' }}
          onChange={e => setNum(e.target.value)}
          min={1}
          max={200}
          value={num} />
        <div className={styles.iconInline}>
          <CTA onClick={onSubmit} disabled={disabled} secondary margins={false} style={{ flex: '0 1 250px', marginRight: '10px' }}>
            <Msg m={messages.generateCodes} />
          </CTA>
          {experience.unlimited && <Icon size={28} i='ios-information-circle-outline' data-tip title={fm(messages.unlimitedMismatch)} /> }
        </div>
      </form>
    </div>
  )
}
